/**
 * Tiny fetch-based API wrapper for the customer portal.
 *
 * Doesn't go through the staff `apiClient` because:
 *   - The staff client has its own JWT + auth-refresh state machine
 *     wired to Redux. Mixing customer + staff tokens on the same axios
 *     instance leads to bizarre cross-talk bugs.
 *   - The portal needs to send a different Authorization header per
 *     request (the customer's token, not the staff's).
 *
 * Same-origin requests via the Vite proxy → no CORS hassle.
 */

import { getPortalSession, setPortalSession } from './portalSession';

const BASE = '/api';

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** Skip the Authorization header (used for OTP request/verify). */
  anonymous?: boolean;
}

export class PortalApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v != null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (!opts.anonymous) {
    const s = getPortalSession();
    if (s?.accessToken) headers.Authorization = `Bearer ${s.accessToken}`;
  }
  const res = await fetch(url.toString(), {
    method,
    headers,
    body: opts.body == null ? undefined : JSON.stringify(opts.body),
  });
  // 401 → drop the session so the portal route guard kicks in.
  if (res.status === 401 && !opts.anonymous) {
    setPortalSession(null);
  }
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const msg =
      (data && typeof data === 'object' && 'message' in data && typeof (data as { message?: unknown }).message === 'string'
        ? (data as { message: string }).message
        : `${method} ${path} failed (${res.status})`);
    throw new PortalApiError(msg, res.status);
  }
  // Unwrap the standard envelope shape (`{ data: T }` or bare T).
  if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
    const inner = (data as { data: unknown }).data;
    if (inner !== undefined) return inner as T;
  }
  return data as T;
}

export const portalApi = {
  get:    <T>(path: string, opts?: RequestOptions) => request<T>('GET',    path, opts),
  post:   <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST',   path, { ...opts, body }),
  put:    <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT',    path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};

/* -------------------------------------------------------------------- */
/*  Helpers — pickList / fmt                                            */
/* -------------------------------------------------------------------- */

export function pickList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
